import * as React from 'react';

import { compose } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';

import { useGetDegreeNameById } from 'bundles/my-purchases-graphql/components/enhancers/useGetDegreeNameById';
import type { DegreeType } from 'bundles/my-purchases-graphql/components/enhancers/useGetDegreeNameById';
import PageDataContext from 'bundles/my-purchases-graphql/data/PageDataContext';
import PageDataController from 'bundles/my-purchases-graphql/data/PageDataController';
import type { PropsFromUseGetSubscriptionById } from 'bundles/my-purchases-graphql/lib/useGetSubscriptionById';
import { useGetSubscriptionById } from 'bundles/my-purchases-graphql/lib/useGetSubscriptionById';
import { useNaptime } from 'bundles/naptimejs';
import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import ProductSkuV1 from 'bundles/naptimejs/resources/productSkus.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import SubscriptionNextBillDetailsV1 from 'bundles/naptimejs/resources/subscriptionNextBillDetails.v1';
import type { OwnableProductByLegacyProductId } from 'bundles/payments-common/hooks/useFindOwnableProducts';
import { useFindOwnableProductByLegacyProductId } from 'bundles/payments-common/hooks/useFindOwnableProducts';
import { mapToLegacyProductId } from 'bundles/payments-common/hooks/useGetProductPrices';
import type { SpecializationType } from 'bundles/payments-common/hooks/useGetSpecializationById';
import { useGetSpecializationById } from 'bundles/payments-common/hooks/useGetSpecializationById';
import type { ProductType } from 'bundles/payments/common/ProductType';
import { useCart } from 'bundles/refund/utils/hooks';

type PropsFromCaller = {
  subscriptionId: string;
  children?: React.ReactNode;
};

export type PropsToProvider = PropsFromCaller &
  PropsFromUseGetSubscriptionById & {
    s12n?: SpecializationType;
    mdp?: DegreeType | null;
    query: Record<string, string>;
    productSku?: ProductSkuV1;
    ownableProduct: OwnableProductByLegacyProductId;
    subscriptionNextBillDetails?: SubscriptionNextBillDetailsV1;
    cart?: CartsV2;
    children: React.ReactNode;
  };

export const useGetSubscriptionRelatedProps = ({ subscriptionId }: PropsFromCaller) => {
  const { subscription } = useGetSubscriptionById({ subscriptionId });

  const {
    isCatalogSubscription,
    isSpecializationSubscription,
    isCourseraLiteSubscription,
    isCourseraPlusSubscription,
  } = subscription ?? {};
  const shouldFetchNextBillingDetails = subscription?.hasSubscriptionNextBillDetails && !subscription.isAppleInApp;
  const {
    data: { subscriptionNextBillDetails },
  } = useNaptime<{ subscriptionNextBillDetails?: SubscriptionNextBillDetailsV1 }>(() => {
    if (!subscriptionId || !shouldFetchNextBillingDetails) {
      return { subscriptionNextBillDetails: null };
    }

    return {
      subscriptionNextBillDetails: SubscriptionNextBillDetailsV1.get(subscriptionId, {
        fields: ['nextBillingPeriodAmount', 'currencyId', 'nextBillingAt', 'remainingNumberOfDiscountedBillingCycles'],
      }),
    };
  }, [subscriptionId]);

  const {
    data: { productSku },
  } = useNaptime<{ productSku?: ProductSkuV1 }>(() => {
    if (!subscription?.productSku || isCatalogSubscription) {
      return { productSku: null };
    }

    return {
      productSku: ProductSkuV1.get(subscription.productSku, {
        fields: ['properties'],
      }),
    };
  }, [subscription?.productSku]);

  const legacyProductId = mapToLegacyProductId({
    productItemId: stringKeyToTuple(subscription.productSku || '')[1],
    productType: stringKeyToTuple(subscription.productSku || '')[0] as ProductType,
  });
  const { ownableProduct } = useFindOwnableProductByLegacyProductId({
    ...legacyProductId,
    skip: !subscriptionId,
  });

  const shouldSkipCart = !(isSpecializationSubscription || isCourseraPlusSubscription || isCourseraLiteSubscription);
  const { cart } = useCart({ cartId: subscription?.cartId, skip: shouldSkipCart });

  return {
    subscription,
    cart,
    subscriptionNextBillDetails,
    productSku,
    ownableProduct,
  };
};

const DataProvider = ({ children, ...data }: PropsToProvider) => {
  const { cart, productSku, subscription, subscriptionNextBillDetails, ownableProduct } =
    useGetSubscriptionRelatedProps({
      subscriptionId: data.subscriptionId,
    });
  const { productItemId, isSpecializationSubscription, isCredentialTrackSubscription } = subscription ?? {};

  const { s12n } = useGetSpecializationById({
    s12nId: `${productItemId}`,
    skip: !isSpecializationSubscription || !productItemId,
  });

  const { mdp } = useGetDegreeNameById({
    degreeId: `${productItemId}`,
    skip: !isCredentialTrackSubscription || !productItemId,
  });

  if (!subscription || (isSpecializationSubscription && !s12n) || (isCredentialTrackSubscription && !mdp)) {
    return null;
  }

  const contextData: PropsToProvider = {
    cart,
    productSku,
    ownableProduct,
    subscription,
    subscriptionNextBillDetails,
    subscriptionId: data.subscriptionId,
    s12n,
    mdp,
    children,
    query: data.query,
  };

  return <PageDataContext.Provider value={new PageDataController(contextData)}>{children}</PageDataContext.Provider>;
};

export default compose<PropsToProvider, PropsFromCaller>(
  connectToRouter<PropsToProvider, PropsFromCaller>(({ location: { query } }) => ({ query }))
)(DataProvider);
